import { ExtractComponentProp } from '@approvalmax/types';
import { Box, Button, Popup, Text, TextField } from '@approvalmax/ui/src/components';
import drop from 'lodash/drop';
import take from 'lodash/take';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { asMutable } from 'modules/immutable';
import { useDispatch } from 'modules/react-redux';
import { memo, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { inviteUsers } from '../../actions';
import { InviteUsersPopupData } from '../../reducers/page/activePopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import { messages } from './InviteUsersPopupContent.messages';

export const InviteUsersPopupContent = memo(() => {
    const dispatch = useDispatch();
    const [messageText, setMessageText] = useState('');

    const company = useSelector(selectors.navigation.getActiveCompany);
    const users = useSelector((state: State) =>
        asMutable(getActivePopup<InviteUsersPopupData>(state).users)
            .map((id) => selectors.user.getUserById(state, id))
            .sort(selectors.user.userComparatorAsc)
    );

    const onMessageTextChange = useCallback<ExtractComponentProp<typeof TextField, 'onChange'>>((messageText) => {
        setMessageText(messageText || '');
    }, []);

    const handleInviteUsers = useCallback(() => {
        dispatch(inviteUsers(company, messageText, users));
    }, [company, dispatch, messageText, users]);

    const usersBlock = useMemo(() => {
        const usersBlock = take(users, 3)
            .map((u, i) => {
                const userBlock = (
                    <span key={u.id} title={u.userEmail}>
                        {u.displayName}
                    </span>
                );

                if (i === 0) {
                    return [userBlock];
                }

                return [', ', userBlock];
            })
            .flat();

        if (users.length > 3) {
            usersBlock.push(
                <span
                    key='others'
                    title={drop(users, 3)
                        .map((x) => `${x.displayName} (${x.userEmail})`)
                        .join('\r\n')}
                >
                    {messages.users({ count: users.length - 3 })}
                </span>
            );
        }

        return usersBlock;
    }, [users]);

    const descriptionText = company.flags.isTrialLicense
        ? messages.descriptionNoInviteMessage({
              companyName: company.displayName,
              users: (
                  <Text font='body' fontWeight='bold' as='span'>
                      {usersBlock}
                  </Text>
              ),
          })
        : messages.description({
              companyName: company.displayName,
              users: (
                  <Text font='body' fontWeight='bold' as='span'>
                      {usersBlock}
                  </Text>
              ),
              br: <br />,
          });

    return (
        <>
            <Popup.Header
                title={messages.title({ count: users.length })}
                actions={
                    <Button
                        size='medium'
                        color='blue80'
                        title={messages.inviteUsersButtonTitle({
                            count: users.length,
                        })}
                        onClick={handleInviteUsers}
                    >
                        {messages.inviteUsersButtonText}
                    </Button>
                }
            />

            <Popup.Body className='fs-mask'>
                <Box>
                    <Text font='body' spacing='0 0 8'>
                        {descriptionText}
                    </Text>

                    {!company.flags.isTrialLicense && (
                        <Box width='100%'>
                            <TextField
                                label={messages.commentTitle}
                                multiline
                                initFocus
                                value={messageText}
                                onChange={onMessageTextChange}
                                placeholder={messages.commentPlaceholder}
                                size='large'
                            />
                        </Box>
                    )}
                </Box>
            </Popup.Body>
        </>
    );
});

InviteUsersPopupContent.displayName = 'InviteUsersPopupContent';
